import { useEffect, useState } from 'react';

/**
 * Хук для загрузки и применения SEO настроек с сервера
 * Обновляет document.title и meta теги в <head>
 */
export const useSEO = () => {
  const [seoSettings, setSeoSettings] = useState({
    metaTitle: null,
    metaDescription: null
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadSEOSettings = async () => {
      try {
        const response = await fetch('/api/settings', {
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Ошибка загрузки SEO настроек');
        }

        const result = await response.json();
        
        if (result.success && result.settings) {
          const settings = {
            metaTitle: result.settings.metaTitle || null,
            metaDescription: result.settings.metaDescription || null
          };
          setSeoSettings(settings);
          applySEOTags(settings);
        }
      } catch (error) {
        console.error('Ошибка загрузки SEO настроек:', error);
        // В случае ошибки используем значения по умолчанию
        applySEOTags({ metaTitle: null, metaDescription: null });
      } finally {
        setLoading(false);
      }
    };

    loadSEOSettings();
  }, []);

  // Применяем SEO теги к документу
  const applySEOTags = (settings) => {
    if (settings.metaTitle) {
      document.title = settings.metaTitle;
    }

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }

    if (settings.metaDescription) {
      metaDescription.setAttribute('content', settings.metaDescription);
    } else {
      const defaultDescription = document.querySelector('meta[name="description"]')?.getAttribute('content') || '';
      if (defaultDescription) {
        metaDescription.setAttribute('content', defaultDescription);
      }
    }
  };

  return { seoSettings, loading };
};

/** Per-page SEO + optional JSON-LD */
export function usePageSEO({ title, description, jsonLd } = {}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    const prevDesc = meta.getAttribute('content');
    if (description) meta.setAttribute('content', description);

    const scriptId = 'page-jsonld';
    let script = document.getElementById(scriptId);
    if (jsonLd) {
      if (!script) {
        script = document.createElement('script');
        script.id = scriptId;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    } else if (script) {
      script.remove();
    }

    return () => {
      document.title = prevTitle;
      if (prevDesc !== null) meta.setAttribute('content', prevDesc || '');
      const s = document.getElementById(scriptId);
      if (s) s.remove();
    };
  }, [title, description, jsonLd]);
};

